//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { PerimeterChainAddressesInterface } from '@react/utils/PerimeterAddresses'
import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { otAssert } from '@shared/conversions'

import { ethers } from 'ethers'

import { StageName } from '@shared/Enums'

import { ProviderReadOnly } from '@shared/SchemaTypes'

import {
  ChainConfiguration,
  findStageConfiguration,
  StageConfiguration,
  UserConnection,
} from '@shared/DeploymentConfigs'
import { logOT } from '@shared/loggingShared'
import { User } from 'oidc-client-ts'
import { ChainAccountValues } from '@shared/LiquidityAsset'

/* eslint  no-template-curly-in-string: off*/

const PREFIX = 'chain'
export interface UserError {
  message: string
  action: string
  timestamp: number
}

export interface ChainConfigurationEnhanced extends ChainConfiguration {
  perimeterAddresses?: PerimeterChainAddressesInterface
  providerReadOnly?: ProviderReadOnly
  chainTimestampDelta: number
}

export interface ChainState {
  isShutdown: boolean
  isLoggedIn: boolean
  isChainConnected: boolean

  stageConfiguration: StageConfiguration
  chainConfiguration: ChainConfigurationEnhanced
  userConnection?: UserConnection
  // calculated

  wsIsConnected?: boolean
  webSocket?: WebSocket
  cognitoUser?: User
}
export type ChainStateInitialized = ChainState & { userConnection: UserConnection }

// Define the initial state using that type

export const initialChainState = getInitialChainState()
export interface ChainWSConnect {
  webSocket: WebSocket
}
export interface OtLocation {
  host: string
  hostname: string
  port: string
  search?: string
}
function getInitialChainState(): ChainState {
  logOT.info('getInitialChainState')
  let _locationStageName: StageName = 'local'
  if (process.env.JEST_WORKER_ID) {
    _locationStageName = 'local'
  } else if (location.hostname === 'localhost') {
    if (location.port === '4020' || location.port === '4010') {
      _locationStageName = 'localsierra'
    } else {
      _locationStageName = 'local'
    }
  } else if (location.hostname.endsWith('opentradedev.com')) {
    _locationStageName = 'dev'
  } else if (location.hostname.endsWith('opentradedevtom.com')) {
    _locationStageName = 'devtom'
  } else if (location.hostname.endsWith('opentradedevotto.com')) {
    _locationStageName = 'devotto'
  } else if (location.hostname.endsWith('opentradeqa.com')) {
    _locationStageName = 'qa'
  } else if (location.hostname.endsWith('sierraqa.com')) {
    _locationStageName = 'qasierra'
  } else if (location.hostname.endsWith('opentradesandbox.com')) {
    _locationStageName = 'sandbox'
  } else if (location.hostname.endsWith('opentradesandboxv5.com')) {
    _locationStageName = 'sandbox'
  } else if (location.hostname.endsWith('opentradesandboxcanary.com')) {
    _locationStageName = 'sandboxcanary'
  } else if (location.hostname.endsWith('opentradecanary.com')) {
    _locationStageName = 'prodcanary'
  } else if (location.hostname.endsWith('open-trade-app.com')) {
    _locationStageName = 'prod'
  } else if (location.hostname.endsWith('open-trade.io')) {
    _locationStageName = 'prod'
  }
  logOT.info('getInitialChainState locationStageName', _locationStageName)
  const stageConfig = findStageConfiguration((localStorage.getItem('stageName') ?? _locationStageName) as StageName)
  let chainConfiguration: ChainConfiguration | undefined
  if (process.env.JEST_WORKER_ID) {
    chainConfiguration = stageConfig.chainConfigurations.find(
      (chainConfig) => chainConfig.chainConfigurationName === 'Hardhat'
    ) as ChainConfiguration
  } else {
    const chainConfigurationName = localStorage.getItem('chainConfigurationName')
    logOT.info('getInitialChainState chainConfigurationName', chainConfigurationName, stageConfig)
    if (chainConfigurationName) {
      chainConfiguration = stageConfig.chainConfigurations.find(
        (chainConfig) => chainConfig.chainConfigurationName === chainConfigurationName
      ) as ChainConfiguration
    }
  }
  if (!chainConfiguration) {
    chainConfiguration = stageConfig.chainConfigurations[0]
  }

  logOT.info('getInitialChainState chainConfiguration', chainConfiguration)
  const chainConfigurationEnhanced: ChainConfigurationEnhanced = {
    ...chainConfiguration,
    chainTimestampDelta: 0,
    perimeterAddresses: undefined,
    providerReadOnly: undefined,
  }
  const initialChainState = {
    isShutdown: false,
    stageConfiguration: stageConfig,
    chainConfiguration: chainConfigurationEnhanced,
    userConnection: undefined,
  }
  return initialChainState as ChainState
}
export const locationToStageName = (location: OtLocation): StageName => {
  if (process.env.JEST_WORKER_ID) {
    return 'local'
  }
  if (location.hostname === 'localhost') {
    return 'local'
  }
  if (location.hostname.endsWith('opentradedev.com')) {
    return 'dev'
  }
  if (location.hostname.endsWith('opentradeqa.com')) {
    return 'qa'
  }

  if (location.hostname.endsWith('opentradesandbox.com')) {
    return 'sandbox'
  }
  if (location.hostname.endsWith('opentradesandboxv5.com')) {
    return 'sandbox'
  }
  if (location.hostname.endsWith('opentradesandboxcanary.com')) {
    return 'sandboxcanary'
  }

  if (location.hostname.endsWith('opentradecanary.com')) {
    return 'prodcanary'
  }
  if (location.hostname.endsWith('open-trade-app.com')) {
    return 'prod'
  }
  if (location.hostname.endsWith('open-trade.io')) {
    return 'prod'
  }
  throw new Error('Unknown location ' + location.hostname)
}

export const chainSlice = createSlice({
  name: PREFIX,
  initialState: initialChainState as ChainState,
  reducers: {
    odicConnect: (chainState: Draft<ChainState>, action: PayloadAction<User>) => {
      chainState.cognitoUser = action.payload
    },
    odicDisconnect: (chainState: Draft<ChainState>, _action: PayloadAction<void>) => {
      chainState.cognitoUser = undefined
    },
    wsConnect: (chainState: Draft<ChainState>, action: PayloadAction<ChainWSConnect>) => {
      logOT.debug('wsConnect')
      chainState.wsIsConnected = true
      chainState.webSocket = action.payload.webSocket
    },
    wsDisconnect: (chainState: Draft<ChainState>, _action: PayloadAction<void>) => {
      logOT.debug('wsDisconnect')
      chainState.wsIsConnected = false
      chainState.webSocket = undefined
    },
    unattachFromStageConfiguration: (chainState: Draft<ChainState>, _action: PayloadAction<void>) => {
      const initialState = getInitialChainState()
      Object.keys(initialState).forEach((key) => {
        const cs = chainState as unknown as Record<string, unknown>
        cs[key] = (initialState as unknown as Record<string, unknown>)[key]
      })
      logOT.debug('unattachFromChainDeployment')
    },
    attachToStageConfiguration: (
      chainState: Draft<ChainState>,
      action: PayloadAction<{ stageConfiguration: StageConfiguration }>
    ) => {
      logOT.debug('chainSLice attachToDeploymentConfiguration')
      chainState.stageConfiguration = action.payload.stageConfiguration
    },
    connectChainConfiguration: (
      chainState: Draft<ChainState>,
      action: PayloadAction<{
        chainConfiguration: ChainConfiguration
        providerReadOnly: ethers.JsonRpcProvider
        chainTimestampDelta: number
        perimeterAddresses: PerimeterChainAddressesInterface
      }>
    ) => {
      logOT.debug('connectChainConfiguration')
      chainState.chainConfiguration = {
        ...action.payload.chainConfiguration,
        chainTimestampDelta: action.payload.chainTimestampDelta,
        perimeterAddresses: action.payload.perimeterAddresses,
        providerReadOnly: action.payload.providerReadOnly,
      }
    },
    disconnectChainConfiguration: (chainState: Draft<ChainState>, _action: PayloadAction<void>) => {
      logOT.info('disconnectChainConfiguration')
      chainState.chainConfiguration.chainTimestampDelta = 0
      chainState.chainConfiguration.perimeterAddresses = undefined
      chainState.chainConfiguration.providerReadOnly = undefined
      chainState.userConnection = undefined
      chainState.wsIsConnected = false
      chainState.isLoggedIn = false
      chainState.webSocket = undefined
    },
    logoffUser: (chainState: Draft<ChainState>, _action: PayloadAction<void>) => {
      logOT.info('disconnectToChain ')
      chainState.userConnection = undefined
      chainState.isLoggedIn = false
    },

    loginUser: (chainState: Draft<ChainState>, action: PayloadAction<UserConnection>) => {
      logOT.info(' connectToChain accountAddr', action.payload.accountAddr, action.payload.roleEnum)
      otAssert(action.payload.accountAddr, 'action.payload.accountAddr')
      otAssert(action.payload.signer, 'action.payload.signer')
      chainState.userConnection = action.payload
      chainState.isLoggedIn = true

      // validate(chainState)
      logOT.info('loginToChain after validate')
    },

    updateChainTimeDelta: (chainState: Draft<ChainState>, action: PayloadAction<{ chainTimestampDelta: number }>) => {
      chainState.chainConfiguration.chainTimestampDelta = action.payload.chainTimestampDelta
    },

    updatePerimeterAddresses: (
      chainState: Draft<ChainState>,
      action: PayloadAction<{ perimeterAddresses: PerimeterChainAddressesInterface }>
    ) => {
      chainState.chainConfiguration.perimeterAddresses = action.payload.perimeterAddresses
    },

    updateAccountBalances: (chainState: Draft<ChainState>, action: PayloadAction<ChainAccountValues>) => {
      otAssert(chainState.userConnection, 'chainState.userConnection')
      chainState.userConnection.accountBalances = action.payload
    },

    resetConnection: (chainState: Draft<ChainState>, _action: PayloadAction<void>) => {
      Object.keys(initialChainState).forEach((key) => {
        const cs = chainState as unknown as Record<string, unknown>
        cs[key] = (initialChainState as unknown as Record<string, unknown>)[key]
      })
    },

    shutDown: (chainState: Draft<ChainState>, __: PayloadAction<void>) => {
      chainState.isShutdown = true
    },
  },
})

export const chainActions = { ...chainSlice.actions }

export default chainSlice
